import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import { quizService, questionService, answerService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { getApiErrorMessage } from '../utils/apiErrors';

const EMPTY_ANSWER = () => ({ text: '', is_correct: false });
const EMPTY_NEW_QUESTION = () => ({
  text: '',
  points: 1,
  explanation: '',
  allow_multiple_answers: false,
  image: null,
  answers: [EMPTY_ANSWER(), EMPTY_ANSWER()],
});

function EditQuiz() {
  const navigate = useNavigate();
  const { quizId } = useParams();
  const { user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    time_limit_minutes: '',
    passing_score: 70,
    randomize_questions: false,
    show_correct_answers: true,
    is_published: false,
  });
  const [errors, setErrors] = useState({});

  // Question management (separate from the quiz-settings form above — each
  // question/answer is its own API resource, saved independently).
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [editQuestionForm, setEditQuestionForm] = useState(null);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQuestionForm, setNewQuestionForm] = useState(EMPTY_NEW_QUESTION());

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const isTeacher = user?.roles?.some(role => role.name?.toLowerCase() === 'teacher');
    if (!isTeacher) {
      addNotification(t('Only teachers can edit quizzes'), 'error');
      navigate('/courses');
      return;
    }

    fetchQuizData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user, quizId]);

  const fetchQuizData = async () => {
    try {
      setFetchingData(true);
      const response = await quizService.getQuiz(quizId);
      const quizData = response.data || response;
      setQuiz(quizData);

      // Set form data
      setFormData({
        title: quizData.title || '',
        description: quizData.description || '',
        time_limit_minutes: quizData.time_limit_minutes || '',
        passing_score: quizData.passing_score || 70,
        randomize_questions: quizData.randomize_questions || false,
        show_correct_answers: quizData.show_correct_answers !== false,
        is_published: quizData.is_published || false,
      });
    } catch (error) {
      addNotification(t('Failed to load quiz data'), 'error');
      navigate('/courses');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = t('Quiz title is required');
    if (formData.passing_score < 0 || formData.passing_score > 100) {
      newErrors.passing_score = t('Passing score must be between 0 and 100');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        title: formData.title,
        description: formData.description,
        time_limit_minutes: formData.time_limit_minutes ? parseInt(formData.time_limit_minutes) : null,
        passing_score: parseInt(formData.passing_score),
        randomize_questions: formData.randomize_questions,
        show_correct_answers: formData.show_correct_answers,
        is_published: formData.is_published,
      };

      await quizService.updateQuiz(quizId, submitData);

      addNotification(t('Quiz updated successfully!'), 'success');
      navigate(-1); // Go back to previous page
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          setErrors(errorData);
          const firstError = Object.values(errorData)[0];
          addNotification(`${t('Failed')}: ${typeof firstError === 'string' ? firstError : JSON.stringify(firstError)}`, 'error');
        } else {
          addNotification(`${t('Failed to update quiz')}: ${errorData}`, 'error');
        }
      } else if (error.message) {
        addNotification(`${t('Failed to update quiz')}: ${error.message}`, 'error');
      } else {
        addNotification(t('Failed to update quiz. Please try again.'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const startEditQuestion = (question) => {
    setExpandedQuestionId(question.id);
    setEditQuestionForm({
      text: question.text,
      points: question.points,
      explanation: question.explanation || '',
      allow_multiple_answers: question.allow_multiple_answers,
      answers: question.answers.map((a) => ({ id: a.id, text: a.text, is_correct: a.is_correct })),
      currentImageUrl: question.image || null,
      image: null,
      removeImage: false,
    });
  };

  const cancelEditQuestion = () => {
    setExpandedQuestionId(null);
    setEditQuestionForm(null);
  };

  const updateEditAnswerField = (idx, field, value) => {
    setEditQuestionForm((prev) => ({
      ...prev,
      answers: prev.answers.map((a, i) => {
        if (i !== idx) {
          // A single-answer question can only have one correct option —
          // selecting a new one clears the others, same as at creation time.
          return field === 'is_correct' && value && !prev.allow_multiple_answers ? { ...a, is_correct: false } : a;
        }
        return { ...a, [field]: value };
      }),
    }));
  };

  const addEditAnswerRow = () => {
    setEditQuestionForm((prev) => ({ ...prev, answers: [...prev.answers, EMPTY_ANSWER()] }));
  };

  const removeEditAnswerRow = (idx) => {
    setEditQuestionForm((prev) => ({ ...prev, answers: prev.answers.filter((_, i) => i !== idx) }));
  };

  const saveEditQuestion = async (question) => {
    const validAnswers = editQuestionForm.answers.filter((a) => a.text.trim());
    if (!editQuestionForm.text.trim() || validAnswers.length < 2) {
      addNotification(t('A question needs text and at least 2 answer options'), 'error');
      return;
    }
    if (!validAnswers.some((a) => a.is_correct)) {
      addNotification(t('Mark at least one answer as correct'), 'error');
      return;
    }

    setSavingQuestion(true);
    try {
      if (editQuestionForm.image || editQuestionForm.removeImage) {
        const fd = new FormData();
        fd.append('text', editQuestionForm.text);
        fd.append('points', parseInt(editQuestionForm.points) || 1);
        fd.append('explanation', editQuestionForm.explanation);
        fd.append('allow_multiple_answers', editQuestionForm.allow_multiple_answers);
        fd.append('image', editQuestionForm.image || '');
        await questionService.updateQuestion(question.id, fd);
      } else {
        await questionService.updateQuestion(question.id, {
          text: editQuestionForm.text,
          points: parseInt(editQuestionForm.points) || 1,
          explanation: editQuestionForm.explanation,
          allow_multiple_answers: editQuestionForm.allow_multiple_answers,
        });
      }

      const originalIds = question.answers.map((a) => a.id);
      const keptIds = editQuestionForm.answers.filter((a) => a.id).map((a) => a.id);
      const removedIds = originalIds.filter((id) => !keptIds.includes(id));

      await Promise.all([
        ...editQuestionForm.answers.map((a) => (a.id
          ? answerService.updateAnswer(a.id, { text: a.text, is_correct: a.is_correct })
          : answerService.createAnswer({ question: question.id, text: a.text, is_correct: a.is_correct, order: 0 }))),
        ...removedIds.map((id) => answerService.deleteAnswer(id)),
      ]);

      addNotification(t('Question updated successfully!'), 'success');
      cancelEditQuestion();
      await fetchQuizData();
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to update question'), 'error');
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!(await confirm(t('Are you sure you want to delete this question?')))) return;

    try {
      await questionService.deleteQuestion(questionId);
      addNotification(t('Question deleted successfully!'), 'success');
      if (expandedQuestionId === questionId) cancelEditQuestion();
      await fetchQuizData();
    } catch (error) {
      addNotification(t('Failed to delete question'), 'error');
    }
  };

  const updateNewAnswerField = (idx, field, value) => {
    setNewQuestionForm((prev) => ({
      ...prev,
      answers: prev.answers.map((a, i) => {
        if (i !== idx) {
          return field === 'is_correct' && value && !prev.allow_multiple_answers ? { ...a, is_correct: false } : a;
        }
        return { ...a, [field]: value };
      }),
    }));
  };

  const addNewAnswerRow = () => {
    setNewQuestionForm((prev) => ({ ...prev, answers: [...prev.answers, EMPTY_ANSWER()] }));
  };

  const removeNewAnswerRow = (idx) => {
    setNewQuestionForm((prev) => ({ ...prev, answers: prev.answers.filter((_, i) => i !== idx) }));
  };

  const handleAddQuestion = async () => {
    const validAnswers = newQuestionForm.answers.filter((a) => a.text.trim());
    if (!newQuestionForm.text.trim() || validAnswers.length < 2) {
      addNotification(t('A question needs text and at least 2 answer options'), 'error');
      return;
    }
    if (!validAnswers.some((a) => a.is_correct)) {
      addNotification(t('Mark at least one answer as correct'), 'error');
      return;
    }

    setSavingQuestion(true);
    try {
      const nextOrder = quiz?.questions?.length || 0;
      let questionResponse;
      if (newQuestionForm.image) {
        const fd = new FormData();
        fd.append('quiz', quizId);
        fd.append('text', newQuestionForm.text);
        fd.append('points', parseInt(newQuestionForm.points) || 1);
        fd.append('order', nextOrder);
        fd.append('allow_multiple_answers', newQuestionForm.allow_multiple_answers);
        if (newQuestionForm.explanation) fd.append('explanation', newQuestionForm.explanation);
        fd.append('image', newQuestionForm.image);
        questionResponse = await questionService.createQuestion(fd, true);
      } else {
        questionResponse = await questionService.createQuestion({
          quiz: quizId,
          text: newQuestionForm.text,
          points: parseInt(newQuestionForm.points) || 1,
          order: nextOrder,
          allow_multiple_answers: newQuestionForm.allow_multiple_answers,
          explanation: newQuestionForm.explanation,
        });
      }

      const newQuestionId = questionResponse.data.id;
      await Promise.all(
        validAnswers.map((a) => answerService.createAnswer({ question: newQuestionId, text: a.text, is_correct: a.is_correct, order: 0 }))
      );

      addNotification(t('Question added successfully!'), 'success');
      setNewQuestionForm(EMPTY_NEW_QUESTION());
      setShowAddQuestion(false);
      await fetchQuizData();
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to add question'), 'error');
    } finally {
      setSavingQuestion(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Edit Quiz')} subtitle={t('Loading...')} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Edit Quiz')} subtitle={quiz?.title || t('Update quiz settings')} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Quiz Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Quiz Title')} *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., Linear Equations Quiz')}
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Description')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={t('Provide a brief description of the quiz...')}
                  />
                </div>

                {/* Time Limit and Passing Score */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="time_limit_minutes" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Time Limit (minutes)')}
                    </label>
                    <input
                      type="number"
                      id="time_limit_minutes"
                      name="time_limit_minutes"
                      value={formData.time_limit_minutes}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder={t('Leave empty for no limit')}
                    />
                    <p className="mt-1 text-xs text-gray-500">{t('Leave empty for no time limit')}</p>
                  </div>

                  <div>
                    <label htmlFor="passing_score" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Passing Score (%)')} *
                    </label>
                    <input
                      type="number"
                      id="passing_score"
                      name="passing_score"
                      value={formData.passing_score}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.passing_score ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.passing_score && <p className="mt-1 text-sm text-red-600">{errors.passing_score}</p>}
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="space-y-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="randomize_questions"
                      checked={formData.randomize_questions}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('Randomize Questions')}</span>
                      <p className="text-xs text-gray-500">{t('Show questions in random order for each attempt')}</p>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="show_correct_answers"
                      checked={formData.show_correct_answers}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('Show results to students after submission')}</span>
                      <p className="text-xs text-gray-500">{t('If off, students only see their score and pass/fail — not which questions they got right or wrong')}</p>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_published"
                      checked={formData.is_published}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">{t('Published')}</span>
                      <p className="text-xs text-gray-500">{t('Make this quiz available to students')}</p>
                    </div>
                  </label>
                </div>

                {/* Quiz Statistics (Read-only) */}
                {quiz && (
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">{t('Quiz Statistics')}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-600">{t('Total Questions')}</p>
                        <p className="text-2xl font-semibold text-gray-800">{quiz.question_count || 0}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-600">{t('Created')}</p>
                        <p className="text-sm font-medium text-gray-800">
                          {new Date(quiz.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('Updating...') : t('Update Quiz')}
                  </button>
                </div>
              </form>
            </div>

            {/* Questions */}
            <div className="bg-white rounded-xl shadow-md p-8 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('Questions')}</h3>

              <div className="space-y-3">
                {(quiz?.questions || []).map((question) => (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                    {expandedQuestionId === question.id ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Question Text')} *</label>
                          <textarea
                            value={editQuestionForm.text}
                            onChange={(e) => setEditQuestionForm((prev) => ({ ...prev, text: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('Points')}</label>
                            <input
                              type="number"
                              min="1"
                              value={editQuestionForm.points}
                              onChange={(e) => setEditQuestionForm((prev) => ({ ...prev, points: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <label className="flex items-center gap-2 mt-6 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editQuestionForm.allow_multiple_answers}
                              onChange={(e) => setEditQuestionForm((prev) => ({ ...prev, allow_multiple_answers: e.target.checked }))}
                              className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{t('Allow multiple correct answers')}</span>
                          </label>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Explanation (shown after answering)')}</label>
                          <textarea
                            value={editQuestionForm.explanation}
                            onChange={(e) => setEditQuestionForm((prev) => ({ ...prev, explanation: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('Question Image (optional)')}</label>
                          {editQuestionForm.currentImageUrl && !editQuestionForm.image && !editQuestionForm.removeImage && (
                            <div className="mb-2 flex items-center gap-3">
                              <img src={editQuestionForm.currentImageUrl} alt="" className="h-16 rounded border border-gray-200" />
                              <button
                                type="button"
                                onClick={() => setEditQuestionForm((prev) => ({ ...prev, removeImage: true }))}
                                className="text-sm text-red-600 hover:text-red-700"
                              >
                                {t('Remove image')}
                              </button>
                            </div>
                          )}
                          {editQuestionForm.removeImage && (
                            <p className="text-sm text-gray-500 mb-2">{t('Image will be removed when saved.')}</p>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setEditQuestionForm((prev) => ({ ...prev, image: e.target.files[0] || null, removeImage: false }))}
                            className="w-full text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('Answer Options')} *</label>
                          <div className="space-y-2">
                            {editQuestionForm.answers.map((answer, idx) => (
                              <div key={answer.id || `new-${idx}`} className="flex items-center gap-2">
                                <input
                                  type={editQuestionForm.allow_multiple_answers ? 'checkbox' : 'radio'}
                                  name={`correct-${question.id}`}
                                  checked={answer.is_correct}
                                  onChange={(e) => updateEditAnswerField(idx, 'is_correct', e.target.checked)}
                                  className="w-5 h-5 text-green-600 shrink-0"
                                  title={t('Correct answer')}
                                />
                                <input
                                  type="text"
                                  value={answer.text}
                                  onChange={(e) => updateEditAnswerField(idx, 'text', e.target.value)}
                                  placeholder={t('Option {{num}}', { num: idx + 1 })}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                {editQuestionForm.answers.length > 2 && (
                                  <button
                                    type="button"
                                    onClick={() => removeEditAnswerRow(idx)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title={t('Remove option')}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={addEditAnswerRow}
                            className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                          >
                            + {t('Add Option')}
                          </button>
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-2">
                          <button
                            type="button"
                            onClick={cancelEditQuestion}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                          >
                            {t('Cancel')}
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEditQuestion(question)}
                            disabled={savingQuestion}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm disabled:opacity-50"
                          >
                            {savingQuestion ? t('Saving...') : t('Save Question')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{question.text}</p>
                            {question.image && (
                              <img src={question.image} alt="" className="h-16 rounded border border-gray-200 mt-2" />
                            )}
                            <ul className="mt-2 space-y-1">
                              {question.answers.map((answer) => (
                                <li key={answer.id} className={`text-sm flex items-center gap-1.5 ${answer.is_correct ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                                  {answer.is_correct ? (
                                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  ) : (
                                    <span className="w-3.5 h-3.5 shrink-0" />
                                  )}
                                  {answer.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-xs text-gray-500 mr-2">{t('{{count}} pts', { count: question.points })}</span>
                            <button
                              type="button"
                              onClick={() => startEditQuestion(question)}
                              className="text-primary-500 hover:text-primary-700 p-1"
                              title={t('Edit Question')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteQuestion(question.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title={t('Delete Question')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {(!quiz?.questions || quiz.questions.length === 0) && !showAddQuestion && (
                  <p className="text-sm text-gray-500 text-center py-6">{t('No questions yet')}</p>
                )}
              </div>

              {showAddQuestion ? (
                <div className="border border-primary-200 bg-primary-50/40 rounded-lg p-4 mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('Question Text')} *</label>
                    <textarea
                      value={newQuestionForm.text}
                      onChange={(e) => setNewQuestionForm((prev) => ({ ...prev, text: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('Points')}</label>
                      <input
                        type="number"
                        min="1"
                        value={newQuestionForm.points}
                        onChange={(e) => setNewQuestionForm((prev) => ({ ...prev, points: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <label className="flex items-center gap-2 mt-6 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newQuestionForm.allow_multiple_answers}
                        onChange={(e) => setNewQuestionForm((prev) => ({ ...prev, allow_multiple_answers: e.target.checked }))}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('Allow multiple correct answers')}</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('Explanation (shown after answering)')}</label>
                    <textarea
                      value={newQuestionForm.explanation}
                      onChange={(e) => setNewQuestionForm((prev) => ({ ...prev, explanation: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('Question Image (optional)')}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setNewQuestionForm((prev) => ({ ...prev, image: e.target.files[0] || null }))}
                      className="w-full text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('Answer Options')} *</label>
                    <div className="space-y-2">
                      {newQuestionForm.answers.map((answer, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type={newQuestionForm.allow_multiple_answers ? 'checkbox' : 'radio'}
                            name="new-question-correct"
                            checked={answer.is_correct}
                            onChange={(e) => updateNewAnswerField(idx, 'is_correct', e.target.checked)}
                            className="w-5 h-5 text-green-600 shrink-0"
                            title={t('Correct answer')}
                          />
                          <input
                            type="text"
                            value={answer.text}
                            onChange={(e) => updateNewAnswerField(idx, 'text', e.target.value)}
                            placeholder={t('Option {{num}}', { num: idx + 1 })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                          {newQuestionForm.answers.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeNewAnswerRow(idx)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title={t('Remove option')}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addNewAnswerRow}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + {t('Add Option')}
                    </button>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { setShowAddQuestion(false); setNewQuestionForm(EMPTY_NEW_QUESTION()); }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                    >
                      {t('Cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleAddQuestion}
                      disabled={savingQuestion}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm disabled:opacity-50"
                    >
                      {savingQuestion ? t('Adding...') : t('Add Question')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAddQuestion(true)}
                  className="mt-4 w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-primary-400 hover:text-primary-600 transition text-sm font-medium"
                >
                  + {t('Add Question')}
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default EditQuiz;
